import { getAttributionForForms } from '../utils/attribution';

// @ts-ignore
interface _ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
   // ...
}

class ContactForm {
  private form!: HTMLFormElement;
  private submitButton!: HTMLButtonElement;
  private isSubmitting: boolean = false;
  private apiUrl: string = '';
  private static instance: ContactForm | null = null;

  constructor() {
      // Pattern Singleton pour éviter les doubles initialisations
      if (ContactForm.instance) {
          console.log("ContactForm déjà initialisé");
          return ContactForm.instance;
      }

      this.form = document.getElementById('contactForm') as HTMLFormElement;
      if (!this.form) {
          console.error("Formulaire de contact non trouvé dans le DOM");
          return;
      }

      this.submitButton = this.form.querySelector('button[type="submit"]') as HTMLButtonElement;
      
      // Déterminer l'URL de l'API en fonction de l'environnement
      this.apiUrl = import.meta.env.VITE_API_URL || 'https://forms.cryptoaccounting.fr';
      
      console.log("✓ Formulaire de contact initialisé");
      console.log("✓ URL API configurée:", this.apiUrl);

      this.initialize();
      ContactForm.instance = this;
  }

  private async initialize(): Promise<void> {
      console.log("Initialisation du formulaire de contact");

      // Nous allons transformer l'événement submit pour afficher le reCAPTCHA modal
      this.form.addEventListener('submit', async (e) => {
          e.preventDefault();

          // Validation des champs
          const inputs = this.form.querySelectorAll('input, textarea');
          let isFormValid = true;

          inputs.forEach(input => {
              if (!this.validateField(input as HTMLInputElement | HTMLTextAreaElement)) {
                  isFormValid = false;
              }
          });

          if (!isFormValid) {
              this.showToast('Veuillez corriger les erreurs dans le formulaire', 'error');
              return;
          }

          // Afficher le modal reCAPTCHA
          await this.showRecaptchaModal();
      });

      // reCAPTCHA chargé à la première interaction avec le formulaire, pas à l'ouverture de la page
      const { loadRecaptchaOnInteraction } = await import('../utils/recaptcha');
      loadRecaptchaOnInteraction(this.form);

      // Validation en temps réel
      const inputs = this.form.querySelectorAll('input, textarea');
      inputs.forEach(input => {
          input.addEventListener('input', () => this.validateField(input as HTMLInputElement | HTMLTextAreaElement));
          input.addEventListener('blur', () => this.validateField(input as HTMLInputElement | HTMLTextAreaElement));
      });

      // Formatage du numéro de téléphone
      const phoneInput = this.form.querySelector('#phone') as HTMLInputElement;
      if (phoneInput) {
          phoneInput.addEventListener('input', (e) => {
              const input = e.target as HTMLInputElement;
              let value = input.value.replace(/\D/g, '');

              if (value.length > 0) {
                  value = value.match(/.{1,2}/g)?.join(' ') || '';
                  if (value.startsWith('33')) {
                      value = '+' + value;
                  }
              }

              input.value = value;
          });
      }
  }

  private async showRecaptchaModal(): Promise<void> {
      // Créer l'overlay du modal
      const overlay = document.createElement('div');
      overlay.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';

      // Créer le contenu du modal
      const modal = document.createElement('div');
      modal.className = 'bg-white rounded-xl p-6 shadow-xl max-w-md w-full mx-4 relative';

      // En-tête du modal
      const header = document.createElement('div');
      header.className = 'mb-4 text-center';
      header.innerHTML = `
          <h3 class="text-xl font-semibold text-primary-900">Vérification de sécurité</h3>
          <p class="text-gray-600 mt-2">Pour finaliser l'envoi de votre message, merci de confirmer que vous n'êtes pas un robot.</p>
      `;

      // Container pour le reCAPTCHA
      const recaptchaContainer = document.createElement('div');
      recaptchaContainer.className = 'flex justify-center my-4';

      // Widget reCAPTCHA - Utilisez un ID unique pour le conteneur
      const recaptchaWidget = document.createElement('div');
      recaptchaWidget.id = 'contact-recaptcha-widget';
      recaptchaWidget.className = 'g-recaptcha';

      // Message d'erreur pour reCAPTCHA
      const errorDiv = document.createElement('div');
      errorDiv.id = 'recaptcha-contact-error';
      errorDiv.className = 'hidden text-red-500 text-sm text-center mt-2';
      errorDiv.textContent = 'Veuillez confirmer que vous n\'êtes pas un robot';

      // Bouton de soumission
      const submitButton = document.createElement('button');
      submitButton.className = 'btn-primary w-full mt-4';
      submitButton.textContent = 'Valider et envoyer';

      // Bouton de fermeture
      const closeButton = document.createElement('button');
      closeButton.className = 'absolute top-3 right-3 text-gray-400 hover:text-gray-600';
      closeButton.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
      `;

      // Assembler le modal
      recaptchaContainer.appendChild(recaptchaWidget);
      modal.appendChild(closeButton);
      modal.appendChild(header);
      modal.appendChild(recaptchaContainer);
      modal.appendChild(errorDiv);
      modal.appendChild(submitButton);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);

      // Le script peut être encore en cours de chargement (chargé à la première interaction)
      const { loadRecaptchaScript } = await import('../utils/recaptcha');
      await loadRecaptchaScript();

      // IMPORTANT: Rendre le reCAPTCHA après avoir ajouté le widget au DOM
      try {
          // @ts-ignore
          grecaptcha.render('contact-recaptcha-widget', {
              'sitekey': import.meta.env.VITE_RECAPTCHA_SITE_KEY,
              'callback': function(response: string) {
                  // Cacher le message d'erreur quand reCAPTCHA est validé
                  if (response) {
                      errorDiv.classList.add('hidden');
                  }
              }
          });
      } catch (error) {
          console.error("Erreur lors du rendu du reCAPTCHA:", error);
      }

      // Gérer les événements
      return new Promise((resolve) => {
          closeButton.addEventListener('click', () => {
              document.body.removeChild(overlay);
              resolve();
          });

          submitButton.addEventListener('click', async () => {
              try {
                  // @ts-ignore
                  const response = grecaptcha.getResponse();

                  if (!response || response.length === 0) {
                      errorDiv.classList.remove('hidden');
                      return;
                  }

                  // Cacher le message d'erreur si reCAPTCHA est valide
                  errorDiv.classList.add('hidden');

                  // Fermer le modal
                  document.body.removeChild(overlay);

                  // Soumettre le formulaire
                  this.handleSubmit(new Event('submit'), response);
              } catch (error) {
                  console.error("❌ Erreur lors de la vérification du reCAPTCHA:", error);
                  errorDiv.classList.remove('hidden');
              }
          });
      });
  }

  private validateField(field: HTMLInputElement | HTMLTextAreaElement): boolean {
    const value = field.value.trim();
    let isValid = true;
    let errorMessage = '';

    field.classList.remove('border-red-500', 'border-green-500');
    this.removeErrorMessage(field);

    switch (field.id) {
        case 'firstName':
        case 'lastName':
            if (value.length < 2) {
                isValid = false;
                errorMessage = 'Ce champ doit contenir au moins 2 caractères';
            }
            break;

        case 'email':
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                isValid = false;
                errorMessage = 'Veuillez entrer une adresse email valide';
            }
            break;

        case 'phone':
            const phoneRegex = /^(\+33|0)[1-9](\d{2}){4}$/;
            const cleanPhone = value.replace(/\s/g, '');
            if (value && !phoneRegex.test(cleanPhone)) {
                isValid = false;
                errorMessage = 'Veuillez entrer un numéro de téléphone valide';
            }
            break;

        case 'message':
            if (value.length < 10) {
                isValid = false;
                errorMessage = 'Votre message doit contenir au moins 10 caractères';
            }
            break;
    }

    if (value) {
        field.classList.add(isValid ? 'border-green-500' : 'border-red-500');
        if (!isValid) {
            this.showErrorMessage(field, errorMessage);
        }
    }

    return isValid;
  }

  private async handleSubmit(e: Event, recaptchaToken: string): Promise<void> {
      e.preventDefault();

      if (this.isSubmitting) return;

      console.log("⚙️ Soumission du formulaire...");

      // Validation des champs (double vérification)
      const inputs = this.form.querySelectorAll('input, textarea');
      let isFormValid = true;

      inputs.forEach(input => {
          if (!this.validateField(input as HTMLInputElement | HTMLTextAreaElement)) {
              isFormValid = false;
          }
      });

      if (!isFormValid) {
          this.showToast('Veuillez corriger les erreurs dans le formulaire', 'error');
          return;
      }

      const formData = new FormData(this.form);
      const firstName = formData.get("firstName") as string;
      const lastName = formData.get("lastName") as string;
      const email = formData.get("email") as string;
      const phone = formData.get("phone") as string;
      const message = formData.get("message") as string;

      try {
          this.isSubmitting = true;
          this.updateSubmitButton(true);
          
          // Déterminer l'URL appropriée en fonction de l'environnement
          const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          const endpoint = isDevelopment ? 'http://localhost:3000/api/send-email' : `${this.apiUrl}/api/send-email`;
          
          console.log(`📧 Envoi du message via: ${endpoint}`);
          console.log("Données à envoyer:", { firstName, lastName, email, phone, messageLength: message.length });
          
          // Tentative d'envoi unique (pas de retry pour éviter la réutilisation du token reCAPTCHA)
          console.log("Tentative d'envoi...");
          
          try {
              const response = await fetch(endpoint, {
                  method: "POST",
                  headers: {
                      "Content-Type": "application/json"
                  },
                  body: JSON.stringify({
                      firstName,
                      lastName,
                      email,
                      phone,
                      message,
                      recaptchaToken,
                      ...getAttributionForForms()
                  })
              });

              const result = await response.json();
              
              if (!response.ok) {
                  console.error("Erreur API:", result);
                  throw new Error(result.error || "Une erreur est survenue lors de l'envoi du message");
              }
              
              // Succès
              console.log("✓ Message envoyé avec succès via l'API:", result);
              
              // Réinitialiser le formulaire en cas de succès
              this.form.reset();
              inputs.forEach(input => {
                  input.classList.remove('border-green-500', 'border-red-500');
              });
              this.showToast('Message envoyé avec succès!', 'success');
              
              return;
              
          } catch (error) {
              console.error("Erreur lors de l'envoi:", error);
              throw error;
          }

      } catch (error) {
          console.error("❌ Erreur:", error);
          
          // Déterminer si c'est une erreur réseau
          if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
              this.showToast('Erreur de connexion au serveur. Veuillez vérifier votre connexion internet et réessayer.', 'error');
          } else if (error instanceof Error && error.message.includes('IP address')) {
              // Message spécifique pour les erreurs d'IP Brevo
              this.showToast('Erreur de configuration du serveur. Veuillez contacter l\'administrateur (problème d\'IP).', 'error');
          } else {
              // Message d'erreur standard
              this.showToast(error instanceof Error ? error.message : 'Une erreur est survenue. Veuillez réessayer.', 'error');
          }
          
          // Logs pour le débogage
          if (error instanceof Error) {
              console.error("Détails de l'erreur:", {
                  name: error.name,
                  message: error.message,
                  stack: error.stack
              });
          } else {
              console.error("Erreur non standard:", error);
          }
      } finally {
          this.isSubmitting = false;
          this.updateSubmitButton(false);
      }
  }

  private updateSubmitButton(isLoading: boolean): void {
      this.submitButton.disabled = isLoading;
      this.submitButton.innerHTML = isLoading ?
          '<svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Envoi en cours...' :
          'Envoyer le message';
  }

  private showErrorMessage(field: HTMLElement, message: string): void {
      const existingError = field.parentElement?.querySelector('.error-message');
      if (existingError) return;

      const errorDiv = document.createElement('div');
      errorDiv.className = 'error-message text-red-500 text-sm mt-1';
      errorDiv.textContent = message;
      field.parentElement?.appendChild(errorDiv);
  }

  private removeErrorMessage(field: HTMLElement): void {
      const errorDiv = field.parentElement?.querySelector('.error-message');
      if (errorDiv) {
          errorDiv.remove();
      }
  }

  private showToast(message: string, type: 'success' | 'error'): void {
      console.log(`Affichage toast: ${type} - ${message}`);
      
      const toast = document.createElement('div');
      toast.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg text-white transform transition-all duration-500 z-50 ${
          type === 'success' ? 'bg-green-500' : 'bg-red-500'
      }`;
      toast.style.opacity = '0';
      toast.textContent = message;
      document.body.appendChild(toast);

      // Force le rendu DOM avant l'animation
      setTimeout(() => {
          toast.style.opacity = '1';
          toast.style.transform = 'translateY(0)';
      }, 10);

      // Disparition du toast après 3 secondes
      setTimeout(() => {
          toast.style.opacity = '0';
          toast.style.transform = 'translateY(10px)';
          
          // Supprimer l'élément après l'animation
          setTimeout(() => {
              if (toast.parentNode) {
                  toast.parentNode.removeChild(toast);
              }
          }, 500);
      }, 3000);
  }
}

// Initialisation
document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('contactForm')) {
    try {
      // @ts-ignore
      const _contactForm = new ContactForm();
      console.log("✓ Formulaire de contact initialisé avec succès");
    } catch (error) {
      console.error("❌ Erreur lors de l'initialisation du formulaire de contact:", error);
    }
  }
});

export default ContactForm;